import { zodResolver } from '@hookform/resolvers/zod';
import {
  customersControllerCreate,
  customersControllerCreateContact,
  customersControllerFindAll,
  customersControllerFindContacts,
} from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  FileCheck,
  FileText,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  Star,
  Upload,
  User,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '../../../components/forms/form-field';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { PhoneInput } from '../../../components/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { emailOptional, uaePhoneOptional } from '../../../lib/validations';

// ── Schemas for inline modals ─────────────────────────────────────────────────

const newAccountModalSchema = z.object({
  orgName: z.string().min(2, 'Organization name must be at least 2 characters'),
  primaryEmail: emailOptional,
  primaryPhone: uaePhoneOptional,
});

const newContactModalSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  email: emailOptional,
  phone: uaePhoneOptional,
  designation: z.string().optional(),
  isPrimary: z.boolean(),
});

interface Account {
  id: string;
  name: string;
  accountType: string;
}

interface Contact {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  designation?: string | null;
  isPrimary: boolean;
}

interface FormData {
  accountId: string;
  accountName: string;
  contactId: string;
  contactName: string;
}

interface Props {
  value: FormData;
  onChange: (updates: Partial<FormData>) => void;
  errors: Partial<Record<keyof FormData, string>>;
}

interface ContactDraft {
  id: number;
  fullName: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

const COMPANY_TYPES = [
  { label: 'General Contractor', value: 'GENERAL_CONTRACTOR' },
  { label: 'Real Estate Developer', value: 'REAL_ESTATE_DEVELOPER' },
  { label: 'Architecture Firm', value: 'ARCHITECTURE_FIRM' },
  { label: 'Specialty Contractor', value: 'SPECIALTY_CONTRACTOR' },
  { label: 'Civil Engineering', value: 'CIVIL_ENGINEERING' },
  { label: 'Electrical Contractor', value: 'ELECTRICAL_CONTRACTOR' },
  { label: 'Company', value: 'COMPANY' },
  { label: 'Individual', value: 'INDIVIDUAL' },
  { label: 'Government', value: 'GOVERNMENT' },
  { label: 'Other', value: 'OTHER' },
];

const emptyContact = (): ContactDraft => ({
  id: Date.now(),
  fullName: '',
  role: '',
  email: '',
  phone: '',
  isPrimary: false,
});

export function AccountContactStep({ value, onChange, errors }: Props) {
  // Account combobox
  const [accountQuery, setAccountQuery] = useState(value.accountName);
  const [accountOpen, setAccountOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState(value.accountName);
  const comboboxRef = useRef<HTMLDivElement>(null);

  // New account modal state
  const [newAccountOpen, setNewAccountOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [sapCode, setSapCode] = useState('');
  const [accountType, setAccountType] = useState('');
  const [tradeLicenseFile, setTradeLicenseFile] = useState<string | null>(null);
  const [vatCertFile, setVatCertFile] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactDraft[]>([
    { id: 1, fullName: '', role: '', email: '', phone: '', isPrimary: true },
  ]);

  // New account modal form (RHF)
  const accountModalForm = useForm<{ orgName: string; primaryEmail: string; primaryPhone: string }>(
    {
      resolver: zodResolver(newAccountModalSchema),
      defaultValues: { orgName: '', primaryEmail: '', primaryPhone: '' },
    },
  );

  // Standalone new-contact modal (for existing accounts)
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);

  const contactModalForm = useForm<{
    fullName: string;
    email: string;
    phone: string;
    designation: string;
    isPrimary: boolean;
  }>({
    resolver: zodResolver(newContactModalSchema) as never,
    defaultValues: { fullName: '', email: '', phone: '', designation: '', isPrimary: false },
  });

  // Sync combobox display text when accountId is set externally (e.g. edit-mode pre-fill)
  useEffect(() => {
    if (value.accountId && value.accountName !== accountQuery) {
      setAccountQuery(value.accountName);
      setDebouncedQuery(value.accountName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.accountId]);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(accountQuery), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [accountQuery]);

  // Close combobox on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: accounts, isFetching: accountsFetching } = useQuery({
    queryKey: ['customers-search', debouncedQuery],
    queryFn: () =>
      customersControllerFindAll({
        ...(debouncedQuery ? { search: debouncedQuery } : {}),
        limit: 10,
      }) as unknown as Promise<Account[]>,
    enabled: accountOpen,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const { data: existingContacts } = useQuery({
    queryKey: ['contacts', value.accountId],
    queryFn: () =>
      customersControllerFindContacts({ id: value.accountId }) as unknown as Promise<Contact[]>,
    enabled: !!value.accountId,
  });

  // Auto-select primary contact when contacts load for a newly selected account
  useEffect(() => {
    if (!existingContacts || value.contactId) return;
    const primary = existingContacts.find((c) => c.isPrimary) ?? existingContacts[0];
    if (primary) {
      onChange({ contactId: primary.id, contactName: primary.fullName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingContacts]);

  function selectAccount(account: Account) {
    onChange({ accountId: account.id, accountName: account.name, contactId: '', contactName: '' });
    setAccountQuery(account.name);
    setAccountOpen(false);
  }

  function resetModal() {
    accountModalForm.reset();
    setSapCode('');
    setAccountType('');
    setTradeLicenseFile(null);
    setVatCertFile(null);
    setContacts([{ id: 1, fullName: '', role: '', email: '', phone: '', isPrimary: true }]);
    setSaveError(null);
  }

  function addContact() {
    setContacts((p) => [...p, { ...emptyContact(), isPrimary: false }]);
  }

  function removeContact(id: number) {
    setContacts((p) => p.filter((c) => c.id !== id));
  }

  function updateContact(id: number, key: keyof Omit<ContactDraft, 'id'>, val: string | boolean) {
    setContacts((p) => p.map((c) => (c.id === id ? { ...c, [key]: val } : c)));
  }

  function setPrimary(id: number) {
    setContacts((p) => p.map((c) => ({ ...c, isPrimary: c.id === id })));
  }

  async function handleCreateAccount(modalData: {
    orgName: string;
    primaryEmail: string;
    primaryPhone: string;
  }) {
    const primary = contacts.find((c) => c.isPrimary) ?? contacts[0];
    if (!primary?.fullName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const created = await (customersControllerCreate({
        requestBody: {
          name: modalData.orgName.trim(),
          accountType: accountType || 'COMPANY',
        },
      }) as unknown as Promise<Account>);

      // Create all contacts; track the primary one for auto-select
      let primaryCreated: Contact | null = null;
      for (const c of contacts) {
        if (!c.fullName.trim()) continue;
        const created_contact = await (customersControllerCreateContact({
          id: created.id,
          requestBody: {
            fullName: c.fullName.trim(),
            email: c.email.trim() || modalData.primaryEmail.trim(),
            phone: c.phone.trim() || modalData.primaryPhone.trim(),
            designation: c.role.trim() || undefined,
            isPrimary: c.isPrimary,
          },
        }) as unknown as Promise<Contact>);
        if (c.isPrimary) primaryCreated = created_contact;
      }

      onChange({
        accountId: created.id,
        accountName: created.name,
        contactId: primaryCreated?.id ?? '',
        contactName: primaryCreated?.fullName ?? '',
      });
      setAccountQuery(created.name);
      setNewAccountOpen(false);
      resetModal();
    } catch {
      setSaveError('Failed to create account. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function createContact(data: {
    fullName: string;
    email: string;
    phone: string;
    designation: string;
    isPrimary: boolean;
  }) {
    if (!value.accountId) return;
    setContactSaving(true);
    try {
      const created = await (customersControllerCreateContact({
        id: value.accountId,
        requestBody: {
          fullName: data.fullName.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          designation: data.designation.trim() || undefined,
          isPrimary: data.isPrimary,
        },
      }) as unknown as Promise<Contact>);
      onChange({ contactId: created.id, contactName: created.fullName });
      setNewContactOpen(false);
      contactModalForm.reset();
    } catch {
      contactModalForm.setError('fullName', {
        message: 'Failed to create contact. Please try again.',
      });
    } finally {
      setContactSaving(false);
    }
  }

  const orgNameWatch = accountModalForm.watch('orgName');
  const primaryContact = contacts.find((c) => c.isPrimary) ?? contacts[0];
  const canCreate = orgNameWatch?.trim() && primaryContact?.fullName.trim() && !saving;

  const docSlots = [
    {
      key: 'trade' as const,
      label: 'Trade License',
      hint: 'Commercial registration document',
      Icon: FileText,
      gradient: 'from-emerald-500 to-teal-600',
      file: tradeLicenseFile,
      setFile: setTradeLicenseFile,
    },
    {
      key: 'vat' as const,
      label: 'VAT Certificate',
      hint: 'Tax registration certificate',
      Icon: FileCheck,
      gradient: 'from-blue-500 to-indigo-600',
      file: vatCertFile,
      setFile: setVatCertFile,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* ── Account combobox ── */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-sm font-semibold text-gray-700">
            Customer Account <span className="text-red-500">*</span>
          </Label>
          <button
            type="button"
            onClick={() => setNewAccountOpen(true)}
            className="flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700"
          >
            <Plus size={12} /> New Account
          </button>
        </div>

        <div ref={comboboxRef} className="relative">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <Input
              type="text"
              placeholder="Search accounts..."
              value={accountQuery}
              onFocus={() => setAccountOpen(true)}
              onChange={(e) => {
                setAccountQuery(e.target.value);
                if (!accountOpen) setAccountOpen(true);
                if (value.accountId)
                  onChange({ accountId: '', accountName: '', contactId: '', contactName: '' });
              }}
              className="pl-9 pr-8"
            />
            {value.accountId ? (
              <button
                type="button"
                onClick={() => {
                  onChange({ accountId: '', accountName: '', contactId: '', contactName: '' });
                  setAccountQuery('');
                  setDebouncedQuery('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear selection"
              >
                <X size={14} />
              </button>
            ) : accountsFetching ? (
              <Loader2
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
              />
            ) : null}
          </div>

          {accountOpen && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
              {accountsFetching && !accounts?.length && (
                <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-400">
                  <Loader2 size={14} className="animate-spin" /> Searching...
                </div>
              )}
              {!accountsFetching && accounts?.length === 0 && (
                <div className="px-3 py-3 text-center text-sm text-gray-400">No accounts found</div>
              )}
              {accounts?.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => selectAccount(account)}
                  className="flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-green-100 text-xs font-bold text-green-700">
                    {account.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{account.name}</div>
                    <div className="text-xs text-gray-400">{account.accountType}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {errors.accountId && <p className="mt-1 text-xs text-red-500">{errors.accountId}</p>}
      </div>

      {/* ── Contact select ── */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-sm font-semibold text-gray-700">
            Primary Contact <span className="text-red-500">*</span>
          </Label>
          {value.accountId && (
            <button
              type="button"
              onClick={() => setNewContactOpen(true)}
              className="flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700"
            >
              <UserPlus size={12} /> New Contact
            </button>
          )}
        </div>

        <Select
          value={value.contactId || undefined}
          onValueChange={(v) => {
            const c = existingContacts?.find((x) => x.id === v);
            onChange({ contactId: v, contactName: c?.fullName ?? '' });
          }}
          disabled={!value.accountId}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a contact..." />
          </SelectTrigger>
          <SelectContent>
            {existingContacts?.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.fullName} {c.isPrimary ? '(Primary)' : ''} — {c.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.contactId && <p className="mt-1 text-xs text-red-500">{errors.contactId}</p>}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          New Account Modal
          ══════════════════════════════════════════════════════════════════════ */}
      {newAccountOpen && (
        <div className="fixed inset-0 z-[100000] flex items-end sm:items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground/15 backdrop-blur-md"
            onClick={() => {
              setNewAccountOpen(false);
              resetModal();
            }}
          />

          <div className="relative bg-card w-full max-w-2xl max-h-[92vh] rounded-3xl border border-border shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between px-7 py-6 border-b border-border shrink-0">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center shadow-lg shadow-primary/30 shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-tight">New Customer</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Create and manage organization accounts
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setNewAccountOpen(false);
                  resetModal();
                }}
                className="w-8 h-8 rounded-xl hover:bg-accent flex items-center justify-center transition-colors shrink-0"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-7 py-6 space-y-7">
              {/* ── Organization Info ── */}
              <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-secondary/10 flex items-center justify-center">
                    <Building2 className="w-3.5 h-3.5 text-secondary" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">Organization Info</h3>
                  <div className="flex-1 h-px bg-border" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <FormField
                      label="Organization Name"
                      required
                      error={accountModalForm.formState.errors.orgName?.message}
                    >
                      <Input
                        {...accountModalForm.register('orgName')}
                        type="text"
                        placeholder="e.g. Acme Construction Inc."
                        hasError={!!accountModalForm.formState.errors.orgName}
                      />
                    </FormField>
                  </div>

                  <FormField label="SAP Code">
                    <Input
                      type="text"
                      value={sapCode}
                      onChange={(e) => setSapCode(e.target.value)}
                      placeholder="e.g. SAP-00472831"
                    />
                  </FormField>

                  <FormField label="Company Type">
                    <Select value={accountType || undefined} onValueChange={setAccountType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>

                  <FormField
                    label="Primary Email"
                    error={accountModalForm.formState.errors.primaryEmail?.message}
                  >
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <Input
                        {...accountModalForm.register('primaryEmail')}
                        type="email"
                        placeholder="info@company.com"
                        className="pl-10"
                        hasError={!!accountModalForm.formState.errors.primaryEmail}
                      />
                    </div>
                  </FormField>

                  <FormField
                    label="Primary Phone"
                    error={accountModalForm.formState.errors.primaryPhone?.message}
                  >
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <PhoneInput
                        {...accountModalForm.register('primaryPhone')}
                        placeholder="+971 50 123 4567"
                        className="pl-10"
                        hasError={!!accountModalForm.formState.errors.primaryPhone}
                      />
                    </div>
                  </FormField>
                </div>
              </div>

              {/* ── Documents ── */}
              <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Upload className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">Documents</h3>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">Optional</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {docSlots.map((doc) => {
                    const DocIcon = doc.Icon;
                    return (
                      <div key={doc.key}>
                        <label className="block text-sm font-semibold text-foreground mb-2">
                          {doc.label}
                        </label>
                        {doc.file ? (
                          <div className="relative rounded-2xl border border-success/30 bg-success/5 p-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${doc.gradient} flex items-center justify-center shrink-0`}
                              >
                                <DocIcon className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate">{doc.file}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Uploaded · PDF
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => doc.setFile(null)}
                                className="w-7 h-7 rounded-lg hover:bg-red-50 hover:text-red-500 flex items-center justify-center text-muted-foreground transition-colors shrink-0"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="block cursor-pointer group">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="sr-only"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) doc.setFile(f.name);
                              }}
                            />
                            <div className="rounded-2xl border-2 border-dashed border-border hover:border-primary/50 bg-background hover:bg-primary/5 transition-all duration-150 p-6 flex flex-col items-center gap-3 text-center">
                              <div className="w-10 h-10 rounded-xl bg-accent group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                                <Upload className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-foreground">{doc.label}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{doc.hint}</p>
                                <p className="text-xs text-muted-foreground/60 mt-1">
                                  PDF, JPG or PNG · Max 10MB
                                </p>
                              </div>
                              <span className="text-xs font-semibold text-primary group-hover:text-primary/80 transition-colors">
                                Click to upload
                              </span>
                            </div>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Contacts ── */}
              <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Users className="w-3.5 h-3.5 text-warning" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">Contacts</h3>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{contacts.length} added</span>
                </div>

                <div className="space-y-3">
                  {contacts.map((c, idx) => (
                    <div
                      key={c.id}
                      className="relative rounded-2xl border border-border bg-background/60 p-4 space-y-3 group hover:border-primary/20 transition-colors"
                    >
                      {/* Contact header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shrink-0">
                            <User className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-xs font-semibold text-foreground">
                            Contact {idx + 1}
                          </span>
                          {c.isPrimary && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary border border-secondary/20 flex items-center gap-1">
                              <Star className="w-2.5 h-2.5" /> Primary
                            </span>
                          )}
                        </div>
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() => removeContact(c.id)}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <X className="w-3 h-3" /> Remove
                          </button>
                        )}
                      </div>

                      {/* Fields */}
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Full Name" required>
                          <Input
                            type="text"
                            value={c.fullName}
                            onChange={(e) => updateContact(c.id, 'fullName', e.target.value)}
                            placeholder="First and last name"
                          />
                        </FormField>
                        <FormField label="Role">
                          <Input
                            type="text"
                            value={c.role}
                            onChange={(e) => updateContact(c.id, 'role', e.target.value)}
                            placeholder="e.g. Project Manager"
                          />
                        </FormField>
                        <FormField label="Email">
                          <Input
                            type="email"
                            value={c.email}
                            onChange={(e) => updateContact(c.id, 'email', e.target.value)}
                            placeholder="contact@company.com"
                          />
                        </FormField>
                        <FormField label="Phone">
                          <PhoneInput
                            value={c.phone}
                            onChange={(e) => updateContact(c.id, 'phone', e.target.value)}
                            placeholder="+971 50 123 4567"
                          />
                        </FormField>
                      </div>

                      {/* Set as primary */}
                      {!c.isPrimary && (
                        <label className="flex items-center gap-2 cursor-pointer group/check">
                          <input
                            type="checkbox"
                            checked={c.isPrimary}
                            onChange={() => setPrimary(c.id)}
                            className="w-4 h-4 rounded accent-primary cursor-pointer"
                          />
                          <span className="text-xs font-medium text-muted-foreground group-hover/check:text-foreground transition-colors">
                            Set as primary contact
                          </span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add Another Contact */}
                <button
                  type="button"
                  onClick={addContact}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 text-sm font-semibold text-muted-foreground hover:text-primary transition-all duration-150 group"
                >
                  <div className="w-5 h-5 rounded-full bg-border group-hover:bg-primary/15 flex items-center justify-center transition-colors">
                    <Plus className="w-3 h-3 group-hover:text-primary" />
                  </div>
                  Add Another Contact
                </button>
              </div>

              {saveError && (
                <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {saveError}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-7 py-5 border-t border-gray-100 bg-white/95 backdrop-blur-sm shrink-0">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="w-1.5 h-1.5 rounded-full bg-warning-500" />
                {orgNameWatch
                  ? `Creating "${orgNameWatch}"`
                  : 'Fill in organization name to continue'}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNewAccountOpen(false);
                    resetModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canCreate}
                  onClick={accountModalForm.handleSubmit(handleCreateAccount)}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Building2 className="w-4 h-4" />
                  )}
                  Create Customer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Standalone New Contact Modal (for existing accounts)
          ══════════════════════════════════════════════════════════════════════ */}
      {newContactOpen && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-foreground/15 backdrop-blur-md"
            onClick={() => setNewContactOpen(false)}
          />
          <div className="relative bg-card w-full max-w-md rounded-3xl border border-border shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between px-7 py-6 border-b border-border">
              <h2 className="text-lg font-bold tracking-tight">New Contact</h2>
              <button
                type="button"
                onClick={() => setNewContactOpen(false)}
                className="w-8 h-8 rounded-xl hover:bg-accent flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="px-7 py-6 space-y-4">
              <FormField
                label="Full Name"
                required
                error={contactModalForm.formState.errors.fullName?.message}
              >
                <Input
                  {...contactModalForm.register('fullName')}
                  type="text"
                  placeholder="First and last name"
                  hasError={!!contactModalForm.formState.errors.fullName}
                />
              </FormField>
              <FormField label="Email" error={contactModalForm.formState.errors.email?.message}>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    {...contactModalForm.register('email')}
                    type="email"
                    placeholder="contact@company.com"
                    className="pl-10"
                    hasError={!!contactModalForm.formState.errors.email}
                  />
                </div>
              </FormField>
              <FormField label="Phone" error={contactModalForm.formState.errors.phone?.message}>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <PhoneInput
                    {...contactModalForm.register('phone')}
                    placeholder="+971 50 123 4567"
                    className="pl-10"
                    hasError={!!contactModalForm.formState.errors.phone}
                  />
                </div>
              </FormField>
              <FormField label="Designation">
                <Input
                  {...contactModalForm.register('designation')}
                  type="text"
                  placeholder="e.g. Project Manager"
                />
              </FormField>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...contactModalForm.register('isPrimary')}
                  className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 cursor-pointer"
                />
                <span className="text-sm text-gray-500">Set as primary contact</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 px-7 py-5 border-t border-gray-100">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewContactOpen(false);
                  contactModalForm.reset();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={contactSaving}
                onClick={contactModalForm.handleSubmit(createContact)}
              >
                {contactSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Create Contact
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
